import React from "react";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import SEO from "../../hooks/SEO";
import PageBanner from "../../components/PageBanner";
import SectionHeader from "../../components/SectionHeader";
import Team from "../../components/Team/Team";
import CapabilityCard from "../../components/CapabilityCard";
import CTASection from "../../components/CTASection";
import {
  companyNarrative,
  portfolioCompanies,
  seoContent,
  siteMeta,
  supportCapabilities,
} from "../../data/mockData";

const About = () => (
  <>
    <SEO
      title={seoContent.about.title}
      description={seoContent.about.description}
      name={siteMeta.name}
      type="website"
    />
    <PageBanner
      title="About Refwa"
      description="Refwa operates across mining, petroleum services, international trade, construction, and related industrial support."
      image={siteMeta.bannerImage}
      breadcrumbs={[{ label: "About" }]}
    />

    <Box className="site-section">
      <Container>
        <Grid container spacing={{ xs: 4, md: 4.5, lg: 5 }} alignItems="stretch" sx={{ width: "100%", m: 0 }}>
          <Grid item xs={12} lg={7}>
            <SectionHeader
              eyebrow="Company Overview"
              title="Company Profile"
              description={companyNarrative.overview}
            />
            <Stack spacing={2.4}>
              {companyNarrative.body.map((paragraph) => (
                <Typography key={paragraph} variant="body1" color="text.secondary" sx={{ lineHeight: 1.88 }}>
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Paper
              sx={{
                p: { xs: 3, md: 3.6 },
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
              }}
            >
              <Box className="eyebrow">Positioning summary</Box>
              <Typography variant="h5" sx={{ mt: 1.5 }}>
                Core Business
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.8 }}>
                Refwa's business is rooted in mining, petroleum services, and international trade, with related capacity across construction, environmental services, facilities, workforce support, equipment, and water-related operations.
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 2.5 }}>
                {companyNarrative.values.map((value) => (
                  <Typography
                    key={value}
                    variant="body2"
                    sx={{
                      px: 1.5,
                      py: 0.95,
                      borderRadius: 3,
                      bgcolor: "rgba(244,238,228,0.6)",
                      border: "1px solid var(--color-border)",
                      color: "text.secondary",
                    }}
                  >
                    {value}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--white">
      <Container>
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          <Grid item xs={12} md={6} lg={4}>
            <Paper
              sx={{
                p: { xs: 2.9, md: 3.3 },
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
                height: "100%",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
              }}
            >
              <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 800 }}>
                Mission
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.5, lineHeight: 1.8, color: "text.secondary" }}>
                {companyNarrative.mission}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper
              sx={{
                p: { xs: 2.9, md: 3.3 },
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
                height: "100%",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
              }}
            >
              <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 800 }}>
                Vision
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.5, lineHeight: 1.8, color: "text.secondary" }}>
                {companyNarrative.vision}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                p: { xs: 2.9, md: 3.3 },
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
                height: "100%",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
              }}
            >
              <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 800 }}>
                Values
              </Typography>
              <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                {companyNarrative.values.map((value) => (
                  <Typography key={value} variant="body1" color="text.secondary">
                    {value}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box className="site-section">
      <Container>
        <SectionHeader
          eyebrow="International Capability"
          title="Operating Capacity"
          description="Refwa's operating capacity spans mining, petroleum services, international trade, construction support, and sustainability-related services."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {supportCapabilities.map((capability) => (
            <Grid item xs={12} md={6} lg={4} key={capability.title}>
              <CapabilityCard {...capability} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--dark">
      <Container>
        <SectionHeader
          eyebrow="Portfolio Breadth"
          title="Group Companies"
          description="These companies reflect capacity across construction, equipment, environmental services, facilities, and water-related work."
          align="center"
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {portfolioCompanies.map((company) => (
            <Grid item xs={12} md={6} lg={4} key={company.id}>
              <Paper
                sx={{
                  p: { xs: 2.9, md: 3.3 },
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 16px 32px rgba(0,0,0,0.12)",
                  color: "#fff",
                }}
              >
                <Typography variant="h6">{company.name}</Typography>
                <Typography variant="subtitle2" sx={{ mt: 1, color: "primary.light" }}>
                  {company.focus}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1.5, color: "rgba(255,255,255,0.76)", lineHeight: 1.8 }}>
                  {company.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section">
      <Container>
        <Team />
      </Container>
    </Box>

    <CTASection
      title="View Services"
      description="Explore Refwa's services and operating capabilities across core sectors and related project support activities."
      primaryLabel="Explore Services"
      primaryTo="/services"
      secondaryLabel="View Portfolio"
      secondaryTo="/portfolio"
    />
  </>
);

export default About;
